import * as React from 'react';
import { DatePicker } from './date-picker';
import { Input } from './input';
import { cn } from 'lib/utils';

export interface DateTimePickerProps {
  /** Value formatted as `YYYY-MM-DDTHH:mm` or an ISO string (or empty when unset). */
  value?: string;
  /** Called with the new `YYYY-MM-DDTHH:mm` string (empty string when cleared). */
  onChange: (value: string) => void;
  /** Inclusive lower bound as `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm`. */
  min?: string;
  /** Inclusive upper bound as `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm`. */
  max?: string;
  /** Default time when selecting a date if no time was set. Defaults to '00:00'. */
  defaultTime?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  ariaLabel?: string;
}

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** Parse date and time components from `YYYY-MM-DDTHH:mm` or ISO string. */
export function splitDateTime(val?: string): { date: string; time: string } {
  if (!val) return { date: '', time: '' };
  if (val.includes('T')) {
    const [d, t = ''] = val.split('T');
    const timeMatch = /^(\d{2}):(\d{2})/.exec(t);
    return {
      date: /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '',
      time: timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : '',
    };
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return { date: val, time: '' };
  }
  const d = new Date(val);
  if (!Number.isNaN(d.getTime())) {
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  }
  return { date: '', time: '' };
}

export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  defaultTime = '00:00',
  placeholder,
  disabled,
  id,
  className,
  ariaLabel,
}: DateTimePickerProps) {
  const { date: datePart, time: timePart } = React.useMemo(() => splitDateTime(value), [value]);

  const minDatePart = React.useMemo(() => (min ? splitDateTime(min).date : undefined), [min]);
  const maxDatePart = React.useMemo(() => (max ? splitDateTime(max).date : undefined), [max]);

  const handleDateChange = (newDate: string) => {
    if (!newDate) {
      onChange('');
      return;
    }
    const t = timePart || defaultTime;
    onChange(`${newDate}T${t}`);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    if (!datePart) return;
    if (!newTime) {
      onChange(`${datePart}T${defaultTime}`);
      return;
    }
    onChange(`${datePart}T${newTime}`);
  };

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <div className="min-w-0 flex-1">
        <DatePicker
          value={datePart}
          onChange={handleDateChange}
          min={minDatePart}
          max={maxDatePart}
          placeholder={placeholder}
          disabled={disabled}
          id={id}
          ariaLabel={ariaLabel ? `${ariaLabel} (date)` : undefined}
          className="w-full"
        />
      </div>
      <div className="relative shrink-0">
        <Input
          type="time"
          value={timePart}
          onChange={handleTimeChange}
          disabled={disabled || !datePart}
          aria-label={ariaLabel ? `${ariaLabel} (time)` : undefined}
          className={cn(
            'h-9 w-[100px] px-2 text-center text-xs font-mono sm:text-sm',
            !datePart && 'cursor-not-allowed opacity-50'
          )}
        />
      </div>
    </div>
  );
}

export default DateTimePicker;
